import React, { useMemo, useState } from 'react';
import { Radio, Card, Avatar, Empty, Typography, Space } from 'antd';
import { CarOutlined, EnvironmentOutlined } from '@ant-design/icons';
import PageLayout from '../../SharedComponents/PageLayout';
import RZMap from '../../SharedComponents/RZMap';
import { useAppSelector } from '../../store/hooks';
import useDriverTracking from '../../hooks/useDriverTracking';
import { getDriverLocationsList, getTrackingConnected } from '../../store/driverTracking.store';
import '../../styles/driver-tracking.scss';

const { Text } = Typography;

const colors = ['#F31559', '#6528F7', '#539165', '#F2BE22', '#090580', '#0079FF', '#C07F00', '#9A208C', '#AA77FF'];

const DriverTrackingPage = (): JSX.Element => {
  useDriverTracking();

  const driverLocations = useAppSelector(getDriverLocationsList);
  const connected = useAppSelector(getTrackingConnected);
  const [selectedDriverId, setSelectedDriverId] = useState<number | null>(null);

  const colorByTripId = useMemo(() => {
    const map: Record<string, string> = {};
    let index = 0;
    driverLocations.forEach((loc) => {
      const key = loc.trip_id !== null ? String(loc.trip_id) : 'no-trip';
      if (!map[key]) {
        map[key] = colors[index % colors.length];
        index += 1;
      }
    });
    return map;
  }, [driverLocations]);

  const validLocations = useMemo(
    () => driverLocations.filter((loc) => !Number.isNaN(Number(loc.latitude)) && !Number.isNaN(Number(loc.longitude))),
    [driverLocations],
  );

  const markerPositions = useMemo(
    () =>
      validLocations.map((loc) => {
        const key = loc.trip_id !== null ? String(loc.trip_id) : 'no-trip';
        return {
          lat: Number(loc.latitude),
          lng: Number(loc.longitude),
          mapColor: colorByTripId[key],
          title: `${loc.license_number}${loc.trip_id ? ` — Trip #${loc.trip_id}` : ''}`,
        };
      }),
    [validLocations, colorByTripId],
  );

  const handleMarkerClick = (index: number) => {
    const loc = validLocations[index];
    if (loc) setSelectedDriverId(loc.driver_id);
  };

  const handleDriverCardClick = (driverId: number) => {
    setSelectedDriverId(driverId);
  };

  const focusedIndex = useMemo(() => {
    if (selectedDriverId === null) return null;
    const idx = validLocations.findIndex((loc) => loc.driver_id === selectedDriverId);
    return idx >= 0 ? idx : null;
  }, [selectedDriverId, validLocations]);

  const timeAgo = (iso: string | null): string => {
    if (!iso) return 'Unknown';
    const diffMs = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  return (
    <PageLayout
      title="Live Driver Tracking"
      actions={[
        <Radio.Group key="status" value={connected ? 'live' : 'disconnected'} buttonStyle="solid">
          <Radio.Button
            value="live"
            className={connected ? 'driver-tracking-page__status-live' : 'driver-tracking-page__status-live--inactive'}
          >
            ● Live
          </Radio.Button>
          <Radio.Button
            value="disconnected"
            className={
              !connected
                ? 'driver-tracking-page__status-disconnected'
                : 'driver-tracking-page__status-disconnected--inactive'
            }
          >
            ● Disconnected
          </Radio.Button>
        </Radio.Group>,
      ]}
    >
      <div className="driver-tracking-page">
        <Card bodyStyle={{ padding: 0 }} className="mg-b-16">
          <div className="driver-tracking-page__map-wrapper">
            <RZMap
              markerPositions={markerPositions}
              showSequenceNumbers={false}
              showWaitingTime={false}
              onMarkerClick={handleMarkerClick}
              focusedIndex={focusedIndex}
            />
          </div>
        </Card>

        <Card title={`Active Drivers (${driverLocations.length})`} bodyStyle={{ padding: '12px 16px' }}>
          {driverLocations.length === 0 ? (
            <Empty description="No driver locations available" />
          ) : (
            <div className="driver-tracking-page__driver-list">
              {driverLocations.map((loc) => {
                const key = loc.trip_id !== null ? String(loc.trip_id) : 'no-trip';
                const color = colorByTripId[key];
                const isSelected = loc.driver_id === selectedDriverId;
                return (
                  <div
                    key={loc.driver_id}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleDriverCardClick(loc.driver_id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleDriverCardClick(loc.driver_id);
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                    className={`driver-tracking-page__driver-card ${
                      isSelected ? 'driver-tracking-page__driver-card--selected' : ''
                    }`}
                  >
                    <Space align="start">
                      <Avatar style={{ backgroundColor: color }} icon={<CarOutlined />} />
                      <div>
                        <div className="driver-tracking-page__driver-name">{loc.license_number}</div>
                        <Text type="secondary" className="driver-tracking-page__driver-meta">
                          {loc.trip_id ? `Trip #${loc.trip_id}` : 'No active trip'}
                        </Text>
                        <br />
                        <Text type="secondary" className="driver-tracking-page__driver-updated">
                          <EnvironmentOutlined /> {timeAgo(loc.time)}
                        </Text>
                      </div>
                    </Space>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </PageLayout>
  );
};

export default DriverTrackingPage;
